<template>
  <el-dialog
    ref="tavernModal"
    v-model="showTavernModal"
    class="tavernModal"
    :class="{
      'ru-locale': $i18n.locale === 'ru',
      tavernModalPusher: checkedGame === TavernGames.pusher,
      tavernModalPusherMobile: checkedGame === TavernGames.pusher && $device.isMobile
    }"
    :fullscreen="$device.isMobile"
    :append-to-body="true"
    :center="true"
    :destroy-on-close="true"
    lock-scroll
    @closed="
      $emit('closed');
      appData?.app.stage.removeChildren();
      appData = null;
      checkedGame = null;
      showModalOverlay = false;
      store.openedBuilding = null;
    "
  >
    <div ref="tavernContainer" class="container">
      <div ref="pixiContainer" class="pixi-container" />
      <div :class="showModalOverlay ? 'modalOverlay' : null" />
      <transition v-if="checkedGame === TavernGames.flipCoin" name="el-fade-in">
        <TavernFlipCoinWrapper
          class="htmlOverlay"
          @start-flipcoin-animation="startAnimationsFacade"
          @start-new-animation="startNewAnimation"
          @skip-animation="skipFlipperAnimation"
          @show-overlay="showOverlayHandler"
          @close-game="showTavernHandler"
        />
      </transition>

      <transition v-if="checkedGame === TavernGames.pusher" name="el-fade-in">
        <TavernPusherWrapper
          class="htmlOverlay"
          @start-new-animation="startNewAnimation"
          @set-background="setBackgroundHandler"
          @close-game="showTavernHandler"
          @show-overlay="showOverlayHandler"
        />
      </transition>

      <component
        :is="activeInfoComponent"
        v-if="showInfoModal"
        style="
          position: relative;
          background: rgba(0, 0, 0, 0.75);
          -webkit-backdrop-filter: blur(2px);
          backdrop-filter: blur(2px);
          border-radius: 14px;
          padding: 20px 0;
        "
        @get-out="
          showInfoModal = false;
          activeInfoComponent = null;
        "
      />
    </div>
    <TavernHeaderLogo v-if="checkedGame !== TavernGames.pusher" :checked-game="checkedGame" />
  </el-dialog>
</template>
<script lang="ts" setup>
import { Spine, type IVertexAttachment } from 'pixi-spine';
import { Application, Assets, Container, type DisplayObject, Matrix, Point, Resource, Sprite, Texture } from 'pixi.js';
import { SmoothGraphics as Graphics } from '@pixi/graphics-smooth';
import type { TNullable } from '~/types/common';
import { TavernGames } from '~/types/tavern';
import { BigNumber } from 'bignumber.js';
import gsap from 'gsap';
import type { FlipCoinGame } from '~/types/FlipCoin';
import { useWeb3ModalAccount } from '@web3modal/ethers/vue';
import type { ConcreteComponent } from 'vue';
import { PusherAnimations } from '~/types/Pusher';
import type { IFlipCoinTokenConfig } from '~/types/apiService';
import { apiUrls } from '~/utils/constants';

const store = useMainStore();

const flipCoinInfo = resolveComponent('TavernFlipCoinInfoModal');
const pusherInfo = resolveComponent('TavernPusherInfoModal');
const { isMobile } = useDevice();
const { login } = useAuth();
const { address } = useWeb3ModalAccount();
const { createVideoResource, createVideoResourceIos } = useVideoResource();
const { createImageResource, createImageResourceIos } = useImageResource();
const { apiUrl } = useEnvs();
const enum SlotEnum {
  CoinPusher = '1',
  FlipCoin = 'table2'
}

interface AnimationOptions {
  duration?: number;
  loop?: boolean;
  alignCenter?: boolean;
}

const infoModal = {
  [SlotEnum.FlipCoin]: flipCoinInfo,
  [SlotEnum.CoinPusher]: pusherInfo
};

const showTavernModal = defineModel<boolean>();
defineEmits(['closed']);

const assetData = await Assets.load(`/assets/animations/tavernInner.skel`);
Assets.add({ alias: 'flipCoinIcon', src: '/img/icons/flipCoin.png' });
Assets.add({ alias: 'coinPusherIcon', src: '/img/icons/pusher.png' });
Assets.add({ alias: 'infoIcon', src: '/img/icons/info.png' });

const appData = shallowRef<ReturnType<typeof createVideoResource> | null>(null);
const tavernContainer = ref<HTMLElement | null>(null);
const pixiContainer = ref<HTMLElement | null>(null);
const checkedGame = ref<TNullable<TavernGames>>(null);
const areFCAnimationsFinished = ref(false);
const tavernAsset = reactive<{ spine?: Spine }>({});
const showModalOverlay = ref(false);
const activeInfoComponent = ref<TNullable<ConcreteComponent | string>>(null);
const showInfoModal = ref(false);
const buttonContainers = ref<Container<DisplayObject>[]>([]);
const currentVideoSprite = ref<Sprite | null>(null);
const skipAnimationResolve = ref<(() => void) | null>(null);

await useFetch<IFlipCoinTokenConfig[]>(apiUrls.flipCoinGame.tokenConfig, {
  baseURL: apiUrl,
  watch: [address],
  key: 'tokensList'
});

provide('animationsFinished', areFCAnimationsFinished);
provide('delayNextAnimation', delayNextAnimation);

watch(showInfoModal, (newIsShow) => {
  if (!appData.value) return;
  if (newIsShow) {
    appData.value.app.ticker.stop();
  } else {
    appData.value.app.ticker.start();
  }
});

const assetsPromise = (async () => {
  const addAsset = (alias: string, src: string) => Assets.add({ alias, src });

  [
    ['piratesVideo', '/pirates.mp4'],
    ['coinFlipRight', '/coinFlipRight.mp4'],
    ['coinFlipLeft', '/coinFlipLeft.mp4'],
    ['skullCoinLeft', '/skullCoinLeft.mp4'],
    ['chestCoinRight', '/chestCoinRight.mp4'],
    ['chestCoinLeft', '/chestCoinLeft.mp4'],
    ['skullCoinRight', '/skullCoinRight.mp4'],
    ['winningPlayerLeft', '/winningPlayerLeft.mp4'],
    ['winningPlayerRight', '/winningPlayerRight.mp4']
  ].forEach(([alias, src]) => addAsset(alias, src));

  const isMobile = useDevice().isMobile;
  const basePath = isMobile ? '/tavern/pusher/mobile' : '/tavern/pusher';
  const bgPath = isMobile ? '/img/tavern/pusher/bg_mobile.jpg' : '/img/tavern/pusher/bg.jpg';

  addAsset('pusherBackground', bgPath);

  Object.values(PusherAnimations).forEach((anim) => {
    const animNameBase = anim.slice(6);
    const animName = animNameBase.charAt(0).toLowerCase() + animNameBase.slice(1);

    return addAsset(anim as string, `${basePath}/${animName}.mp4`);
  });
  await Assets.load([
    'piratesVideo',
    'coinFlipRight',
    'coinFlipLeft',
    'skullCoinLeft',
    'chestCoinRight',
    'chestCoinLeft',
    'skullCoinRight',
    'winningPlayerLeft',
    'winningPlayerRight',
    'pusherBackground',
    ...Object.values(PusherAnimations)
  ]);
})();

watch(
  pixiContainer,
  async (el) => {
    if (!el || appData.value) return;

    try {
      let result;
      try {
        result = createVideoResource('doorVideo', pixiContainer);
      } catch (error) {
        result = await createVideoResourceIos('door', pixiContainer);
      }

      appData.value = result;

      const { app, videoSprite, videoResource, videoElement } = result;

      el.innerHTML = '';
      el.appendChild(app.view as unknown as HTMLCanvasElement);
      app.stage.addChild(videoSprite);

      const resetVideo = () => {
        [videoResource.source, videoElement].forEach((video) => {
          video.pause();
          video.currentTime = 0;
        });
        app.stage.removeChild(videoSprite);
        onCompleteDoorAnimation();
      };

      videoResource.source.onended = resetVideo;
      videoElement.onended = resetVideo;

      videoSprite.eventMode = 'dynamic';
      videoSprite.cursor = 'pointer';
      videoSprite.on('pointerdown', () => {
        gsap.to(videoSprite, {
          alpha: 0,
          duration: 0.5,
          onComplete: resetVideo
        });
      });

      try {
        videoResource.source.currentTime = 0;
        await videoResource.source.play();

        videoElement.currentTime = 0;
        await videoElement.play();
      } catch (err) {
        console.warn('Video play failed, user interaction may be required:', err);
      }

      await assetsPromise;
    } catch (error) {
      console.error('Error initializing Pixi video resources:', error);
    }
  },
  { immediate: true }
);

const onCompleteDoorAnimation = () => {
  setupTavernScene();
};

async function setupTavernScene() {
  if (!appData.value) {
    console.error('setupTavernScene: appData is null — cannot continue');
    return;
  }
  const spine = new Spine(assetData.spineData);
  tavernAsset.spine = spine;

  const { app, tavernWidth, tavernHeight } = appData.value!;
  const SCALE_FACTOR = 1.3;

  const getOffset = (dimensionSize: number, scaleFactor: number) =>
    BigNumber(dimensionSize).multipliedBy(scaleFactor).minus(dimensionSize);

  const xOffset = getOffset(tavernWidth, SCALE_FACTOR);
  const yOffset = getOffset(tavernHeight, SCALE_FACTOR);

  const scale = {
    width: BigNumber(tavernWidth).multipliedBy(SCALE_FACTOR).dividedBy(assetData.spineData?.width).toNumber(),
    height: BigNumber(tavernHeight).multipliedBy(SCALE_FACTOR).dividedBy(assetData.spineData?.height).toNumber()
  };

  spine.zIndex = 2;
  spine.x = -xOffset.dividedBy(2).toNumber();
  spine.y = -yOffset.dividedBy(2).toNumber();
  spine.scale.set(isMobile ? scale.width * 4 : scale.width, scale.height);

  if (isMobile) {
    spine.pivot.x = spine.width / 4;
  }
  app.stage.addChild(spine);

  spine.autoUpdate = true;
  spine.eventMode = 'dynamic';
  spine.state.setAnimation(0, 'idle', true);

  const handleClick = createGameStartClickHandler(app);
  createGameButton(spine, SlotEnum.FlipCoin, scale, () => handleClick(TavernGames.flipCoin)).then(
    (coinPusherButton) => {
      app.stage.addChild(coinPusherButton);
    }
  );

  createGameButton(spine, SlotEnum.CoinPusher, scale, () => handleClick(TavernGames.pusher)).then(
    (coinPusherButton) => {
      app.stage.addChild(coinPusherButton);
    }
  );
}

function showTavernHandler() {
  if (appData.value && assetData) {
    checkedGame.value = null;
    destroyVideoSprite();
    setupTavernScene();
  }
}

function showOverlayHandler(show: boolean) {
  showModalOverlay.value = show;
}

async function setBackgroundHandler() {
  const app = appData.value?.app;
  if (!app) return;

  try {
    const { sprite } = createImageResource('pusherBackground', app);
    app.stage.addChild(sprite);
  } catch {
    const { sprite } = await createImageResourceIos('/img/tavern/pusher/bg_mobile.jpg', app);
    app.stage.addChild(sprite);
  }
}

function destroyButtonContainers() {
  buttonContainers.value.forEach((buttonContainer) => {
    buttonContainer.destroy();
  });
}

function destroyVideoSprite() {
  if (!appData.value) {
    return;
  }

  if (!currentVideoSprite.value) {
    return;
  }

  const { app } = appData.value;
  app.stage.removeChild(currentVideoSprite.value as Sprite);
  currentVideoSprite.value.destroy();
  currentVideoSprite.value = null;
}

function createGameStartClickHandler(app: Application) {
  return async (game: TavernGames) => {
    if (game === TavernGames.pusher && !address.value) {
      await login();
      return;
    }

    destroyButtonContainers();

    if (tavernAsset.spine instanceof Spine) {
      await gsap.to(tavernAsset.spine, {
        alpha: 0,
        duration: 0.5,
        onComplete: () => {
          app.stage.removeChildren();
        }
      });
    }

    checkedGame.value = game;

    if (checkedGame.value === TavernGames.pusher) {
      try {
        const { sprite } = createImageResource('pusherBackground', app);
        app.stage.addChild(sprite);
      } catch {
        const { sprite } = await createImageResourceIos('/img/tavern/pusher/bg_mobile.jpg', app);
        app.stage.addChild(sprite);
      }
      return;
    }

    let piratesVideoResult;
    try {
      piratesVideoResult = createVideoResource('piratesVideo', pixiContainer, true);
    } catch {
      piratesVideoResult = await createVideoResourceIos('pirates', pixiContainer, true);
    }

    const { videoSprite, videoResource, videoElement } = piratesVideoResult;
    currentVideoSprite.value = videoSprite;

    videoSprite.alpha = 0;
    app.stage.addChild(videoSprite);

    try {
      videoResource.source.currentTime = 0;
      videoElement.currentTime = 0;

      await Promise.all([videoResource?.source?.play(), videoElement?.play()]);
    } catch (err) {
      console.warn('Pirates video failed to play (interaction required?):', err);
    }

    gsap.to(videoSprite, {
      alpha: 1,
      duration: 0.5
    });
  };
}

function delayNextAnimation(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const skipFlipperAnimation = async () => {
  if (skipAnimationResolve.value) {
    skipAnimationResolve.value();
    skipAnimationResolve.value = null;

    await startNewAnimation('piratesVideo', { duration: 0.5, loop: true });
  }
};

async function startAnimationsFacade(startedGame: FlipCoinGame) {
  if (!startedGame) return;
  const { ownerPlayer, isOddBet, winner } = startedGame;
  const isOwnerMe = isComparedAddressMe(ownerPlayer.userAddress);
  const isOwnerWon = didOwnerWon(winner, ownerPlayer.userAddress);

  areFCAnimationsFinished.value = false;

  let resolveSkipAnimation: () => void;
  const skipAnimationPromise = new Promise<void>((resolve) => {
    resolveSkipAnimation = resolve;
  });

  skipAnimationResolve.value = resolveSkipAnimation!;

  const firstAnimationName = getCoinFlipperAnimationName(1, isOwnerMe, isOddBet, isOwnerWon);
  await startNewAnimation(firstAnimationName);

  await Promise.race([delayNextAnimation(300), skipAnimationPromise]);

  if (skipAnimationResolve.value === null) {
    return;
  }

  const secondAnimationName = getCoinFlipperAnimationName(2, isOwnerMe, isOddBet, isOwnerWon);
  await startNewAnimation(secondAnimationName);

  await Promise.race([delayNextAnimation(300), skipAnimationPromise]);

  if (skipAnimationResolve.value === null) {
    return;
  }

  const thirdAnimationName = getCoinFlipperAnimationName(3, isOwnerMe, isOddBet, isOwnerWon);
  await startNewAnimation(thirdAnimationName);

  await Promise.race([delayNextAnimation(700), skipAnimationPromise]);

  if (skipAnimationResolve.value === null) {
    return;
  }

  areFCAnimationsFinished.value = true;

  await startNewAnimation('piratesVideo', { duration: 0.5, loop: true });
}

const isComparedAddressMe = (addressToCompare: string): boolean => {
  if (!address.value) return false;

  return address.value.toLowerCase() === addressToCompare.toString().toLowerCase() ? true : false;
};

const didOwnerWon = (winnerAddress: string, ownerAddress: string): boolean => {
  return winnerAddress.toLowerCase() === ownerAddress.toLowerCase() ? true : false;
};

function getAnimationFilePath(animationFileName: string): string {
  // Обработка pusher анимаций (начинаются с префикса "pusher")
  if (animationFileName.startsWith('pusher')) {
    const isMobile = useDevice().isMobile;
    const basePath = isMobile ? 'tavern/pusher/mobile' : 'tavern/pusher';

    const animNameBase = animationFileName.slice(6);
    const animName = animNameBase.charAt(0).toLowerCase() + animNameBase.slice(1);

    return `${basePath}/${animName}`;
  }

  // Обработка coin flip анимаций
  // Убираем суффикс 'Video' если присутствует (например, 'piratesVideo' → 'pirates')
  if (animationFileName.endsWith('Video')) {
    return animationFileName.slice(0, -5);
  }

  // Для остальных coin flip анимаций возвращаем имя как есть
  return animationFileName;
}

async function startNewAnimation(
  animationFileName: string,
  options: AnimationOptions = { duration: 0.5, loop: false, alignCenter: false }
) {
  if (!appData.value) {
    return;
  }
  const { app } = appData.value;
  const { duration, loop, alignCenter } = options;

  if (tavernAsset.spine instanceof Spine) {
    await gsap.to(tavernAsset.spine, {
      alpha: 0,
      duration,
      onComplete: () => {
        app.stage.removeChildren();
      }
    });
  }

  let videoSprite: any;
  let videoElement: HTMLVideoElement;
  let videoResource: any;

  try {
    const result = createVideoResource(animationFileName, tavernContainer, loop);
    videoSprite = result.videoSprite;
    videoElement = result.videoElement;
    videoResource = result.videoResource;
  } catch (error: any) {
    const filePath = getAnimationFilePath(animationFileName);
    const result = await createVideoResourceIos(filePath, tavernContainer, loop);
    videoSprite = result.videoSprite;
    videoElement = result.videoElement;
    videoResource = result.videoResource;
  }

  if (alignCenter) {
    const canvasWidth = app.screen.width;
    const canvasHeight = app.screen.height;

    videoSprite.width = canvasWidth;
    videoSprite.height = canvasHeight;

    videoSprite.anchor.set(0.5);
    videoSprite.x = canvasWidth / 2;
    videoSprite.y = canvasHeight / 2;
  }

  currentVideoSprite.value = videoSprite;
  videoSprite.alpha = 0;

  app.stage.addChild(videoSprite);
  try {
    videoResource.source.currentTime = 0;
    videoElement.currentTime = 0;

    await Promise.all([videoResource?.source?.play(), videoElement?.play()]);
  } catch (err) {
    console.warn('Pirates video failed to play (interaction required?):', err);
  }

  gsap.to(videoSprite, {
    alpha: 1,
    duration
  });

  return new Promise<void>((resolve) => {
    videoElement.onended = () => {
      resolve();
    };
    videoResource.source.onended = () => {
      resolve();
    };
  });
}

function getCoinFlipperAnimationName(
  animationNumber: number,
  isOwnerMe: boolean,
  isOddBet: boolean,
  isOwnerWon: boolean
): string {
  let animationFile = '';

  if (animationNumber === 1 && isOwnerMe) animationFile = 'coinFlipRight';
  else if (animationNumber === 1 && !isOwnerMe) animationFile = 'coinFlipLeft';
  else if (animationNumber === 2 && isOwnerWon && isOddBet) animationFile = 'skullCoinLeft';
  else if (animationNumber === 2 && !isOwnerWon && isOddBet) animationFile = 'chestCoinRight';
  else if (animationNumber === 2 && isOwnerWon && !isOddBet) animationFile = 'chestCoinLeft';
  else if (animationNumber === 2 && !isOwnerWon && !isOddBet) animationFile = 'skullCoinRight';
  else if (animationNumber === 3 && isOwnerWon) animationFile = 'winningPlayerLeft';
  else if (animationNumber === 3 && !isOwnerWon) animationFile = 'winningPlayerRight';

  return animationFile;
}

const showGameInfoModal = (game: SlotEnum) => {
  activeInfoComponent.value = infoModal[game];
  showInfoModal.value = true;
};

function createGameButtonGradient(): Texture<Resource> {
  const gradientCanvas = document.createElement('canvas');
  gradientCanvas.width = 56;
  gradientCanvas.height = 56;

  const ctx = gradientCanvas.getContext('2d')!;
  const gradient = ctx.createRadialGradient(28, 28, 5, 28, 28, 28);
  gradient.addColorStop(0, '#fbd18b');
  gradient.addColorStop(1, '#6c3f19');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 56, 56);

  const gradientTexture = Texture.from(gradientCanvas);

  return gradientTexture;
}

async function createGameButton(
  spineAsset: Spine,
  tavernSlot: SlotEnum,
  scale: { width: number; height: number },
  clickHandler?: () => void
): Promise<Container> {
  const flipCoinSlot = spineAsset.skeleton.findSlot(tavernSlot);
  const flipCoinTable = flipCoinSlot.getAttachment() as IVertexAttachment;

  const worldVertices = new Float32Array(flipCoinTable.worldVerticesLength);
  flipCoinTable.computeWorldVertices(flipCoinSlot, 0, flipCoinTable.worldVerticesLength, worldVertices, 0, 2);

  const slotX = worldVertices[0];
  const slotY = worldVertices[1];

  const globalPoint = spineAsset.toGlobal(new Point(slotX, slotY));

  const buttonContainer = new Container();
  buttonContainers.value.push(buttonContainer);

  const button = new Graphics().beginTextureFill({
    texture: createGameButtonGradient(),
    matrix: new Matrix(1, 0, 0, 1, -28, -28)
  });

  button.lineStyle({
    width: 2,
    color: 0xffffff,
    alpha: 0.7
  });

  button.alpha = 0.7;
  button.interactive = true;
  button.cursor = 'pointer';

  button.drawCircle(0, 0, 28);
  button.endFill();

  buttonContainer.addChild(button);

  await Assets.load(['coinPusherIcon', 'flipCoinIcon', 'infoIcon']);
  const iconTexture = Assets.get(tavernSlot === SlotEnum.CoinPusher ? 'coinPusherIcon' : 'flipCoinIcon');
  const icon = Sprite.from(iconTexture);
  icon.anchor.set(0.5);
  icon.width = icon.height = 36;
  icon.x = button.x;
  icon.y = button.y;

  buttonContainer.addChild(icon);

  const infoIcon = Sprite.from(Assets.get('infoIcon'));
  infoIcon.anchor.set(0, 1);
  infoIcon.width = infoIcon.height = 24;
  infoIcon.x = 28;
  infoIcon.y = -28;
  infoIcon.interactive = true;
  infoIcon.cursor = 'pointer';

  buttonContainer.addChild(infoIcon);

  buttonContainer.zIndex = 4;

  //@ts-ignore
  const xPadding = tavernSlot === SlotEnum.CoinPusher ? 25 : 0;
  const yPadding = tavernSlot === SlotEnum.CoinPusher ? 80 : 0;
  buttonContainer.x = globalPoint.x + button.width / 2 - xPadding;

  //@ts-ignore
  buttonContainer.y = globalPoint.y - flipCoinTable.height * scale.height + button.height - yPadding;

  if (isMobile) {
    buttonContainer.scale.y = 0.8;
    buttonContainer.scale.x = 1.7;
  }

  if (clickHandler) {
    button.on('pointerdown', async () => {
      clickHandler();
    });
    button.on('pointerover', () => {
      gsap.to(button, {
        alpha: 1,
        duration: 0.3
      });
    });
    button.on('pointerout', () => {
      gsap.to(button, {
        alpha: 0.7,
        duration: 0.3
      });
    });
  }

  infoIcon.on('pointerdown', () => {
    showGameInfoModal(tavernSlot);
  });

  return buttonContainer;
}
</script>

<style lang="scss" scoped>
.container,
.pusher-container {
  height: 100%;
  width: 100%;
  border-radius: 14px;
}

// pirates animation
.pixi-container {
  position: absolute;
  width: 100% !important;
  height: 100% !important;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
}

// black overlay for fake modal windows
.modalOverlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(5px);
  z-index: 2;
  width: 100%;
}

// html (lists and so on)
.htmlOverlay {
  position: relative;
  z-index: 3;
}
</style>

<style lang="scss">
.el-dialog.tavernModal {
  margin: 0;
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 906px;
  max-width: 906px;
  height: 678px;
  position: relative;
  padding: 0;

  @media screen and (max-width: 479px) {
    width: 100vw;
    height: 100dvh;
    padding: 0;
    overflow: hidden;
  }

  canvas {
    width: 100% !important;
    height: 100% !important;
    display: block;
  }

  .el-dialog__body {
    max-width: 100%;
    max-height: 100%;
    height: 100%;
    padding-bottom: 0;
    padding-top: 90px;
  }
}
</style>
